import React, { useEffect } from 'react'
import { withRouter } from 'react-router-dom'

import { useAccountState } from '@acorns/web-bank-linking'
import { Spinner } from '@acorns/web-components'
import { withFormik } from 'formik'
import { compose, fromRenderProps, withHandlers, withProps, withState } from 'recompose'

import BankLinkSuccessAccordion from 'assets/images/bank-link-success-accordion.svg'
import Button from 'components/Button'
import withRemoteStrings, { RemoteStringsProps } from 'components/HOCs/withRemoteStrings'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import Separator from 'components/Separator'
import { withClickTrack } from 'components/Trackers/ClickTracker'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { ActionType, LinkedSubaccount, PageName } from 'generated/types'
import trackEvent from 'utils/analytics/trackEvent'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelContext, FunnelContextProps } from 'utils/FunnelContext'
import { withSetPrimaryFundingSource } from 'utils/mutators/bank-linking/bankLinking'
import { withOptimizely } from 'utils/Optimizely'
import { withPrompt } from 'utils/prompts'

import {
  resolveChooseFundingButtonTappedEvent,
  resolveChooseFundingViewedEvent,
} from '../Institutions/DirectIntegration/events'
import Account from './Account'
import { FormValues, handleSubmit, mapPropsToValues, validationSchema } from './form'
import {
  Container,
  FormContainer,
  FormError,
  Image,
  Link,
  StyledBody,
  Text,
  Title,
} from './styled-components'

type Props = {
  handleSelectChange: (field: string, value: string) => void
  valid?: boolean
  errors: Partial<FormValues>
  values: FormValues
  header: string
  body: string
  submitCta: string
  submitting?: boolean
  financialInstitutionLogoUrl?: string
  linkedSubaccounts: LinkedSubaccount[]
}

type HandlerProps = {
  handleChange: () => void
  handleDifferentBank: () => void
  handleManualLink: () => void
  submitForm?: () => void
  setSubmitting?: (value: boolean) => void
}

type InnerProps = Props &
  HandlerProps &
  RemoteStringsProps &
  WithFlowRouterProps &
  FunnelContextProps

const ChooseFundingSourceButton = withClickTrack<any, any>(
  TRACKER_IDS.CHOOSE_FUNDING_SOURCE.CONTINUE_BUTTON,
)(Button)

const DEFAULT_COPY = {
  header: 'Choose your primary account',
  body: 'Every time you add money to Acorns, the money will transfer from this account.',
  submitCta: 'Next',
}

export const enhance = compose<InnerProps, {}>(
  fromRenderProps<Partial<FunnelContextProps>, {}, Partial<FunnelContextProps>>(
    FunnelContext.Consumer,
    ({ isBackEnabled, isDynamicFunnelEnabled, routeToPage }) => ({
      isBackEnabled,
      isDynamicFunnelEnabled,
      routeToPage,
    }),
  ),
  withState('selectedAccountId', 'setSelectedAccountId', null),
  withState('submitting', 'setSubmitting', false),
  withRouter,
  withFlowRouter,
  withOptimizely,
  withSetPrimaryFundingSource,
  withPrompt,
  withRemoteStrings,
  withFormik({
    mapPropsToValues,
    validationSchema,
    handleSubmit,
    validateOnChange: true,
  }),
  withProps(({ getRemoteString, location, values }) => {
    if (!location.state || !location.state.linkedSubaccounts) {
      return {
        linkedSubaccounts: null,
      }
    } else {
      const linkedSubaccounts = location.state.linkedSubaccounts.map(subAccount => {
        return {
          accountNumberLastFour: subAccount.accountNumberLastFour,
          id: subAccount.id,
          name: subAccount.name,
        }
      })
      return {
        financialInstitutionLogoUrl: location.state?.financialInstitution?.logoUrl,
        header: getRemoteString('choose_funding_source_header', DEFAULT_COPY.header),
        body: getRemoteString('choose_funding_source_body', DEFAULT_COPY.body),
        submitCta: getRemoteString('choose_funding_source_submit_cta', DEFAULT_COPY.submitCta),
        linkedSubaccounts,
        valid: Object.values(values).every(Boolean),
      }
    }
  }),
  withHandlers({
    handleSelectChange: ({ setFieldValue, linkedSubaccounts, setSelectedAccountId }) => (
      field,
      name,
    ) => {
      const selectedOption = linkedSubaccounts.find(account => account.name === name)
      setSelectedAccountId(selectedOption.id)
      setFieldValue(field, name)
    },
    handleDifferentBank: ({ route, isDynamicFunnelEnabled, routeToPage }) => () => {
      isDynamicFunnelEnabled
        ? routeToPage(PageName.ChooseFundingSource, ActionType.Switch)
        : route({ from: FlowRoute.ChooseFundingSource, action: 'different-bank' })
    },
    handleManualLink: ({ route, isDynamicFunnelEnabled, routeToPage }) => () => {
      isDynamicFunnelEnabled
        ? routeToPage(PageName.ChooseFundingSource, ActionType.Manual)
        : route({ from: FlowRoute.ChooseFundingSource, action: 'manual' })
    },
  }),
)

export const ChooseFundingSource: React.FC<InnerProps> = ({
  errors,
  financialInstitutionLogoUrl,
  header,
  body,
  submitCta,
  handleDifferentBank,
  handleManualLink,
  selectedAccountId,
  submitForm,
  handleSelectChange,
  valid,
  submitting,
  linkedSubaccounts,
  flow,
  isDynamicFunnelEnabled,
  isBackEnabled,
}) => {
  const { financialInstitution } = useAccountState()

  useEffect(() => {
    if (financialInstitution?.linkProviderType === 'DIRECT_BANK') {
      trackEvent('Screen Viewed', {
        eventData: resolveChooseFundingViewedEvent(financialInstitution.name),
        flow,
      })
    }
  }, [financialInstitution])

  const handleSubmitClick = () => {
    if (financialInstitution?.linkProviderType === 'DIRECT_BANK') {
      trackEvent('Button Tapped', {
        eventData: {
          ...resolveChooseFundingButtonTappedEvent(financialInstitution.name),
          cta_title: DEFAULT_COPY.submitCta,
        },
        flow,
      })
    }

    submitForm()
  }

  return (
    <>
      <Nav back={isDynamicFunnelEnabled && isBackEnabled(PageName.ChooseFundingSource)} />
      <Layout>
        {linkedSubaccounts ? (
          <>
            <Image src={BankLinkSuccessAccordion} />
            <Title>{header}</Title>
            <Text>{body}</Text>
            <FormContainer onSubmit={handleSubmit}>
              {linkedSubaccounts.map(subaccount => (
                // Disabling ESLint for this line for no reason other than the code existed like this before this ESLint rule went into effect.
                // If you're here from the future, please resolve this ESLint error!
                // As-is, this may lead to unpredictable, difficult-to-detect behavior from React.
                // eslint-disable-next-line react/jsx-key
                <Account
                  subaccount={subaccount}
                  handleSelectChange={handleSelectChange}
                  selectedAccountId={selectedAccountId}
                  financialInstitutionLogoUrl={financialInstitutionLogoUrl}
                />
              ))}
              {errors && <FormError>{errors.fundingSource}</FormError>}
            </FormContainer>
            <Separator />
            <ChooseFundingSourceButton
              styleType={!valid ? 'invalid' : undefined}
              onPress={handleSubmitClick}
              disabled={submitting}
              buttonType={'submit'}
            >
              {submitting ? <Spinner height={20} width={20} white={true} /> : submitCta}
            </ChooseFundingSourceButton>
          </>
        ) : (
          <Container>
            <StyledBody>
              {
                "We couldn't find a checking account to use as your funding source. Please link a bank that has a checking account."
              }
            </StyledBody>
            <Button onPress={handleDifferentBank}>{'Link another bank'}</Button>
            <Link onClick={handleManualLink}>{'Link with routing/account number'}</Link>
          </Container>
        )}
      </Layout>
    </>
  )
}

export default enhance(ChooseFundingSource)
